import {
  PanoramaControls,
  Quaternion,
  Vector3,
  Web3DViewer,
} from '@technology/web3d';
import { ThreeJsManager, type PickResult } from '@trimble/utilities';
import {
  PositioningData,
  StationsCapabilityProvider,
  Web3dTool,
} from '@trimble/web3d-providers';
import {
  Accessor,
  createEffect,
  createMemo,
  createSignal,
  Setter,
} from 'solid-js';
import { NavigationalCapabilityBase } from './base/navigational-capability-base';
import { DEFAULT_FIELD_OF_VIEW } from '../helpers/camera/camera-constants';
import { StationNavigationController } from './models/station-navigation-controller';
import {
  PANORAMA_CAMERA_LOCAL_FORWARD,
  PANORAMA_HORIZON_LAT,
} from './constants/panorama-constants';
import { NavigationTool } from '../helpers/web3d-tool-helper';
import {
  StationViewManager,
  type StationVisualsConfig,
} from '../helpers/station/station-view-manager';

// Re-exported because external code imports this type from
// `@trimble/web3d-capabilities`.
export type { StationVisualsConfig };

/**
 * @intent Navigation business logic for a single viewer's stations:
 * selected-index tracking (with identity preserved across list reindexes),
 * camera position + trajectory-relative yaw, and active-run sync. All
 * visuals are delegated to {@link StationViewManager}.
 *
 * Subclass hooks for {@link MapStationCapability} and `ImageCapabilityBase`:
 *   - {@link getStationZ} — visual Z resolver.
 *   - {@link handlePick} — pick reaction.
 *   - {@link shouldShowStationMarkers} — visibility gate.
 *   - `trackIsEnabled` — provider-driven enabled state.
 */
export class StationsNavigationCapability
  extends NavigationalCapabilityBase
  implements StationNavigationController
{
  protected override requiredWeb3DTools: NavigationTool[] = [
    Web3dTool.PanoramaControls,
  ];

  public isFirstStation: Accessor<boolean>;
  public isLastStation: Accessor<boolean>;
  public readonly selectedStationIndex: Accessor<number | null>;
  private readonly setSelectedStationIndex: Setter<number | null>;
  public readonly currentStationPosition: Accessor<Vector3 | null>;

  public readonly isStationMarkersVisible: Accessor<boolean>;
  public readonly setIsStationMarkersVisible: Setter<boolean>;

  public readonly isNavigationPathVisible: Accessor<boolean>;
  public readonly setIsNavigationPathVisible: Setter<boolean>;

  /** Visual pipeline; subclasses and tests reach view state through this. */
  public readonly viewManager: StationViewManager;

  /** Pinned identity so the selection survives a station-list reindex. */
  private preservedStationKey: string | null = null;
  private preservedStationRunId: string | null = null;

  constructor(
    viewer: Web3DViewer,
    protected readonly stationsProvider: StationsCapabilityProvider,
    threeJsManager: ThreeJsManager,
    private readonly canShowStationMarkers?: Accessor<boolean>,
    config?: StationVisualsConfig
  ) {
    super(viewer, stationsProvider);

    const [rawIndex, setRawIndex] = createSignal<number | null>(null, {
      equals: false,
    });
    this.setSelectedStationIndex = setRawIndex;

    // The raw signal keeps the last explicitly-set value; this memo
    // remaps it to the live list using `preservedStationKey/RunId` so
    // consumers always see a correct index after a reindex.
    this.selectedStationIndex = createMemo(() => {
      const rawIdx = rawIndex();
      if (rawIdx == null) {
        this.preservedStationKey = null;
        this.preservedStationRunId = null;
        return null;
      }

      const info = this.stationsProvider.stationsInfo();
      let currIndex = rawIdx;

      if (this.preservedStationKey != null && info.length > 0) {
        const keyAtIndex =
          currIndex < info.length
            ? StationViewManager.positionKey(info[currIndex].position)
            : null;

        if (keyAtIndex !== this.preservedStationKey) {
          const newIdx = info.findIndex(
            (s) =>
              StationViewManager.positionKey(s.position) ===
                this.preservedStationKey &&
              s.runId === this.preservedStationRunId
          );

          if (newIdx !== -1) {
            currIndex = newIdx;
          } else {
            // Run unloaded. Index 1 is the default because the first
            // station typically only has panorama data, not planar.
            currIndex = info.length > 1 ? 1 : 0;
          }
        }
      }

      const currStation = info[currIndex];
      if (!currStation?.position) {
        this.preservedStationKey = null;
        this.preservedStationRunId = null;
        return null;
      }

      this.preservedStationKey = StationViewManager.positionKey(
        currStation.position
      );
      this.preservedStationRunId = currStation.runId;
      return currIndex;
    });

    this.currentStationPosition = createMemo(() => {
      const idx = this.selectedStationIndex();
      if (idx == null) return null;
      const info = this.stationsProvider.stationsInfo();
      const pos = info[idx]?.position;
      if (!pos) return null;
      return new Vector3(pos.x, pos.y, pos.z);
    });

    const [isMarkersVisible, setIsMarkersVisible] = createSignal<boolean>(true);
    this.isStationMarkersVisible = isMarkersVisible;
    this.setIsStationMarkersVisible = setIsMarkersVisible;

    const [isNavigationPathVisible, setIsNavigationPathVisible] =
      createSignal<boolean>(true);
    this.isNavigationPathVisible = isNavigationPathVisible;
    this.setIsNavigationPathVisible = setIsNavigationPathVisible;

    this.viewManager = new StationViewManager(
      viewer,
      threeJsManager,
      {
        stationsInfo: this.stationsProvider.stationsInfo,
        isEnabled: this.isEnabled,
        isActive: this.isActive,
        currentStationPosition: this.currentStationPosition,
        // Routed through methods/late-bound arrows so subclass overrides
        // (`MapStationCapability`, `ImageCapabilityBase`) take effect even
        // though the manager is built before subclass field initializers run.
        canShowStationMarkers: () => this.shouldShowStationMarkers(),
        getStationZ: (pos) => this.getStationZ(pos),
        onStationPicked: (result) => this.handlePick(result),
        isStationMarkersVisible: this.isStationMarkersVisible,
        isNavigationPathVisible: this.isNavigationPathVisible,
      },
      config
    );

    // Visual side-effects of selection (dimming) live on the view manager.
    createEffect(() => {
      const position = this.currentStationPosition();
      if (!position) return;
      this.viewer.camera.position = new Vector3(
        position.x,
        position.y,
        position.z
      );
    });

    this.isFirstStation = createMemo(() => {
      const currentIndex = this.selectedStationIndex();
      return currentIndex === 0;
    });

    this.isLastStation = createMemo(() => {
      return (
        this.selectedStationIndex() === this.stationsProvider.stationCount() - 1
      );
    });

    createEffect(() => {
      const idx = this.selectedStationIndex();
      if (idx == null) {
        this.stationsProvider.setActiveRunId(null);
        return;
      }
      const info = this.stationsProvider.stationsInfo();
      const runId = info[idx]?.runId ?? null;
      this.stationsProvider.setActiveRunId(runId);
    });

    createEffect(() => {
      const active = this.isActive();
      if (!active) {
        this.setSelectedStationIndex(null);

        // PanoramaControls let the user zoom, which mutates FOV; reset
        // to default whenever station navigation deactivates.
        this.viewer.camera.fieldOfView = DEFAULT_FIELD_OF_VIEW;
      }
    });
  }

  protected shouldShowStationMarkers(): boolean {
    return this.isEnabled() && (this.canShowStationMarkers?.() ?? true);
  }

  protected getStationZ(stationPosition: Vector3): number {
    return stationPosition.z - this.viewManager.config.stationHeightOffset;
  }

  /**
   * Default: navigate to the picked station. {@link MapStationCapability}
   * overrides to set a picked-position signal instead.
   */
  protected readonly handlePick = (result: PickResult): void => {
    const point = this.viewManager.findStationPointForPickResult(result);
    if (!point) {
      return;
    }

    this.goToStation(point.stationIndex);
  };

  public override activate(): boolean {
    if (!this.isActive() && this.isEnabled()) {
      const panoramaControls = this.viewer.tools.get<
        PanoramaControls | undefined
      >(PanoramaControls);
      if (panoramaControls) {
        this.setPanoramaControlsFromQuaternion(
          panoramaControls,
          this.viewer.camera.quaternion,
          PANORAMA_HORIZON_LAT
        );
      }
    }
    return super.activate();
  }

  public goToFirstStation(): void {
    if (this.stationsProvider.stationCount() === 0) return;
    this.goToStation(0);
  }

  public goToLastStation(): void {
    const count = this.stationsProvider.stationCount();
    if (count === 0) return;
    this.goToStation(count - 1);
  }

  public goToStation(index: number): void {
    // Clear preserved identity so the memo treats this as intentional
    // navigation rather than a list reindex.
    this.preservedStationKey = null;
    this.preservedStationRunId = null;

    if (index < 0 || index > this.stationsProvider.stationCount() - 1) {
      this.setSelectedStationIndex(null);
      return;
    }

    let oldTrajectoryAngle: number | null = null;

    const previousIndex = this.selectedStationIndex();
    if (previousIndex != null) {
      oldTrajectoryAngle = this.getTrajectoryDirectionAngle(previousIndex);
    }

    this.setSelectedStationIndex(index);
    this.activate();

    if (oldTrajectoryAngle != null) {
      const newTrajectoryAngle = this.getTrajectoryDirectionAngle(index);
      if (newTrajectoryAngle != null) {
        this.adjustCameraForTrajectoryChange(
          oldTrajectoryAngle,
          newTrajectoryAngle
        );
      }
    }
  }

  public getCurrentStationIndex(): number | null {
    return this.selectedStationIndex();
  }

  public goToPreviousStation(): void {
    const currentIndex = this.selectedStationIndex();
    if (currentIndex == null) {
      return;
    }

    this.goToStation(currentIndex - 1);
  }

  public goToNextStation(): void {
    let currentIndex = this.selectedStationIndex();

    if (currentIndex == null) {
      const stationCount = this.stationsProvider.stationCount();
      // Default starts at index 0 → goToStation(1); planar data typically
      // exists only from the second station onwards.
      currentIndex = stationCount > 1 ? 0 : -1;
    } else if (currentIndex >= this.stationsProvider.stationCount() - 1) {
      currentIndex = -1;
    }

    this.goToStation(currentIndex + 1);
  }

  public applyCameraOrientation(quaternion: Quaternion, fov?: number): void {
    this.viewer.camera.quaternion = quaternion;
    if (fov != null) {
      this.viewer.camera.fieldOfView = fov;
    }
    const panoramaControls = this.viewer.tools.get<
      PanoramaControls | undefined
    >(PanoramaControls);
    if (panoramaControls) {
      this.setPanoramaControlsFromQuaternion(panoramaControls, quaternion);
    }
  }

  public getFrustumPosition(): Omit<PositioningData, 'cameraPosition'> {
    return {
      targetPosition: this.viewer.camera.position,
      quaternion: this.viewer.camera.quaternion.toArray(),
      fov: this.viewer.camera.fieldOfView,
    };
  }

  private setPanoramaControlsFromQuaternion(
    panoramaControls: PanoramaControls,
    quaternion: Quaternion,
    fixedPanoramaLatitude?: number
  ): void {
    const forward =
      PANORAMA_CAMERA_LOCAL_FORWARD.clone().applyQuaternion(quaternion);
    panoramaControls.lon = Math.atan2(forward.y, forward.x);
    panoramaControls.lat =
      fixedPanoramaLatitude ?? Math.acos(Math.max(-1, Math.min(1, forward.z)));
    panoramaControls.updateCamera();
  }

  public async cleanup(): Promise<void> {
    await this.viewManager.cleanup();
  }

  /**
   * Yaw in the XY plane to the next same-run station, or from the previous
   * one if at the end of a run. Null when no same-run neighbour exists.
   */
  private getTrajectoryDirectionAngle(index: number): number | null {
    const stationsInfo = this.stationsProvider.stationsInfo();
    const station = stationsInfo[index];
    if (!station) return null;

    const nextStation = stationsInfo[index + 1];
    if (nextStation && nextStation.runId === station.runId) {
      const dx = nextStation.position.x - station.position.x;
      const dy = nextStation.position.y - station.position.y;
      return Math.atan2(dy, dx);
    }

    const prevStation = stationsInfo[index - 1];
    if (prevStation && prevStation.runId === station.runId) {
      const dx = station.position.x - prevStation.position.x;
      const dy = station.position.y - prevStation.position.y;
      return Math.atan2(dy, dx);
    }

    return null;
  }

  /**
   * Pre-multiplies a Z-axis delta rotation so the user's relative look
   * direction survives a station change without Euler decomposition.
   */
  protected adjustCameraForTrajectoryChange(
    oldTrajectoryAngle: number,
    newTrajectoryAngle: number
  ): void {
    const cameraQuaternion = this.viewer.camera.quaternion;
    if (!cameraQuaternion) return;

    const deltaAngle = newTrajectoryAngle - oldTrajectoryAngle;
    const deltaQuat = new Quaternion().setFromAxisAngle(
      new Vector3(0, 0, 1),
      deltaAngle
    );
    this.viewer.camera.quaternion = deltaQuat.multiply(cameraQuaternion);
  }
}
